import { useState } from "react";
import { MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MediaUploader } from "@/components/Media";
import { AuditList } from "@/components/AuditList";
import { StatusBadge } from "@/components/shared";
import { api, errMsg, fdt } from "@/lib/api";

const QUICK = ["Sent to brand service center", "Replacement approved", "Replacement received", "Dispatched to client"];

export function RmaDetail({ rma, onClose, onUpdate }) {
  const [note, setNote] = useState("");
  const [auditKey, setAuditKey] = useState(0);
  if (!rma) return null;
  const post = async (path, body, msg) => {
    try { onUpdate((await api.post(`/rma/${rma.id}/${path}`, body)).data); setAuditKey((k) => k + 1); toast.success(msg); return true; }
    catch (e) { toast.error(errMsg(e)); return false; }
  };
  const addNote = async (content) => { if (await post("comments", { content }, "Update logged")) setNote(""); };
  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="rma-detail-drawer">
        <SheetHeader><SheetTitle className="flex items-center gap-2"><span className="font-mono">{rma.rmaNumber}</span><StatusBadge status={rma.status} /></SheetTitle></SheetHeader>
        <div className="mt-4 space-y-1 text-sm">
          <div className="font-medium">{rma.partName}</div>
          <div className="text-xs text-muted-foreground">Serial {rma.serialNumber || "—"} · Vendor ref {rma.vendorRmaRef || "—"}</div>
          <div className="text-sm text-muted-foreground pt-2">{rma.issueDescription}</div>
        </div>
        <div className="mt-6">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Condition media</div>
          <MediaUploader items={rma.conditionMedia || []} testid="rma-media" onAdd={(m) => post("media", m, "Media added")} />
        </div>
        <Tabs defaultValue="updates" className="mt-6">
          <TabsList><TabsTrigger value="updates" data-testid="rma-tab-updates">Updates</TabsTrigger><TabsTrigger value="activity" data-testid="rma-tab-activity">Activity log</TabsTrigger></TabsList>
          <TabsContent value="updates" className="space-y-3 mt-3">
            <div className="flex flex-wrap gap-1.5">{QUICK.map((q) => (
              <button key={q} onClick={() => addNote(q)} className="text-[11px] rounded-full border px-2.5 py-1 hover:border-primary hover:text-primary" data-testid={`rma-quick-${q.split(" ")[0].toLowerCase()}`}>{q}</button>
            ))}</div>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Log a progress note" data-testid="rma-comment-input" />
            <Button size="sm" onClick={() => addNote(note)} disabled={!note.trim()} data-testid="rma-comment-submit"><MessageSquare className="h-3.5 w-3.5 mr-1.5" />Post update</Button>
            <div className="space-y-2" data-testid="rma-comment-list">
              {[...(rma.comments || [])].reverse().map((c) => (
                <div key={c.id} className="rounded-md border p-3 text-sm">
                  <div className="flex text-xs mb-1"><span className="font-medium">{c.authorName}</span><span className="ml-auto text-muted-foreground">{fdt(c.createdAt)}</span></div>
                  {c.content}
                </div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="activity" className="mt-3"><AuditList entityType="RMA" entityId={rma.id} refreshKey={auditKey} /></TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
